/**
 * motion.js — the ONE shared anime.js (v4) motion system for the whole app.
 *
 * Presets:
 *   LIVELY (house default) — scroll-triggered fade + 26px slide-up, out(3),
 *     ~650ms, optional child stagger (130ms, capped at 8). `fast` variant
 *     (14px / 450ms / 80ms) for the OS screens staff use daily.
 *   WOW (reserved) — spring/scale-in. Used ONLY for: marketing hero on load,
 *     Workshop "team is building" cards, Blueprint verdict reveal.
 *
 * Hard rules honoured here, centrally:
 *   · prefers-reduced-motion  → every hook becomes a no-op, content static.
 *   · Progressive enhancement → markup renders visible; JS hides just before
 *     first paint then animates. If JS fails, nothing is ever hidden.
 *   · transform + opacity only → no layout props, no CLS, smooth on mobile.
 *   · Reveals fire once (IntersectionObserver disconnects after trigger).
 *   · Original inline opacity/transform are remembered and restored.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { animate, createSpring } from 'animejs';

const STAGGER_CAP = 8;

export const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const remember = (t) => {
  if (!t.__kOrig) t.__kOrig = { opacity: t.style.opacity, transform: t.style.transform };
};
const hide = (t) => { remember(t); t.style.opacity = '0'; };
const restore = (t) => {
  if (t.__kOrig) {
    t.style.opacity = t.__kOrig.opacity;
    t.style.transform = t.__kOrig.transform;
    delete t.__kOrig;
  }
};

// ── LIVELY — scroll reveal, fires once ─────────────────────────────────────
export function useReveal({ delay = 0, staggerChildren = false, staggerMs = 130, fast = false } = {}) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || prefersReduced()) return;
    const targets = staggerChildren ? Array.from(el.children) : [el];
    if (!targets.length) return;
    targets.forEach(hide);
    let anim = null;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        anim = animate(targets, {
          opacity: [0, 1],
          translateY: [fast ? 14 : 26, 0],
          duration: fast ? 450 : 650,
          ease: 'out(3)',
          delay: (t, i) => delay + Math.min(i, STAGGER_CAP - 1) * (fast ? 80 : staggerMs),
          onComplete: () => targets.forEach(restore),
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    io.observe(el);
    return () => { io.disconnect(); anim?.cancel?.(); targets.forEach(restore); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ref;
}

export const Lively = ({ as: As = 'div', delay = 0, staggerChildren = false, fast = false, children, ...rest }) => {
  const ref = useReveal({ delay, staggerChildren, fast });
  return <As ref={ref} {...rest}>{children}</As>;
};

// ── WOW — on-load spring/scale intro (marketing hero only) ────────────────
export function useWowIntro({ staggerChildren = true, staggerMs = 110 } = {}) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || prefersReduced()) return;
    const targets = staggerChildren ? Array.from(el.children) : [el];
    if (!targets.length) return;
    targets.forEach(hide);
    const anim = animate(targets, {
      opacity: { from: 0, to: 1, duration: 520, ease: 'out(2)' },
      translateY: [34, 0],
      scale: [0.96, 1],
      ease: createSpring({ stiffness: 140, damping: 16 }),
      delay: (t, i) => 80 + Math.min(i, STAGGER_CAP - 1) * staggerMs,
      onComplete: () => targets.forEach(restore),
    });
    return () => { anim?.cancel?.(); targets.forEach(restore); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ref;
}

export const WowIntro = ({ as: As = 'div', children, ...rest }) => {
  const ref = useWowIntro();
  return <As ref={ref} {...rest}>{children}</As>;
};

// ── WOW — spring-in for elements that appear dynamically ──────────────────
// (Workshop team cards as they're added, Blueprint verdict banner.)
export function useSpringIn(deps = []) {
  const ref = useRef(null);
  const played = useRef(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || played.current || prefersReduced()) return;
    played.current = true;
    hide(el);
    const anim = animate(el, {
      opacity: { from: 0, to: 1, duration: 340, ease: 'out(2)' },
      translateY: [16, 0],
      scale: [0.9, 1],
      ease: createSpring({ stiffness: 210, damping: 17 }),
      onComplete: () => restore(el),
    });
    return () => { anim?.cancel?.(); restore(el); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}

// ── Stat count-up — anime-driven, fires once when scrolled into view ──────
export function useCountUp(target, { duration = 1400 } = {}) {
  const [val, setVal] = useState(() => (prefersReduced() ? target : 0));
  const ref = useRef(null);
  useEffect(() => {
    if (prefersReduced()) { setVal(target); return; }
    const el = ref.current;
    if (!el) { setVal(target); return; }
    let anim = null;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const obj = { v: 0 };
        anim = animate(obj, {
          v: target,
          duration,
          ease: 'out(3)',
          onUpdate: () => setVal(Math.round(obj.v)),
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => { io.disconnect(); anim?.cancel?.(); };
  }, [target, duration]);
  return [ref, val];
}

export const CountUp = ({ value, duration = 900, prefix = '', suffix = '', format, ...rest }) => {
  const target = Number(value) || 0;
  const [ref, val] = useCountUp(target, { duration });
  const fmt = format || ((n) => n.toLocaleString('en-AU'));
  return <span ref={ref} {...rest}>{prefix}{fmt(val)}{suffix}</span>;
};
